/**
 * An `EmailDocument` into the HTML an inbox will actually render — 16 Sep 2026.
 *
 * Tables and inline styles, because that is what email is. Outlook renders
 * through Word's HTML engine, which has no flexbox, no grid, no `max-width` on a
 * div and no external stylesheet; Gmail strips `<style>` from a forwarded
 * message. A nested table with `bgcolor` and inline CSS is the one construction
 * every client has agreed on for twenty years.
 *
 * ## The components are the designer's, and so are their defaults
 *
 * The header, heading, dated notice, stat row, card, steps and footer come from
 * the email handover of 17 Sep 2026, and the values here — 26px/600 headings,
 * the 60px pill on the button, #F4F6F8 panels, 40px side padding on a 600px
 * column — are that document's. They are components rather than things a writer
 * styles by hand so that the defaults are right without anybody remembering
 * them. See `EMAIL_THEME`.
 *
 * ## Conditions are marked, not resolved, here
 *
 * A section addressed at one kind of reader is wrapped in a comment pair:
 *
 *   <!--skida:if=trial,free--> … <!--skida:end-->
 *
 * The server cuts the sections a given reader should not see, per tier, and the
 * markers never reach an inbox. Doing it this way means the renderer exists
 * **once**, in the browser, where the preview needs it — the Cloud Functions
 * bundle is a separate TypeScript program and cannot import from `src/`, so a
 * server-side renderer would be a second copy of every table in this file, kept
 * in step by hand. What the server needs instead is twenty lines of string
 * cutting, which is `stripTiers` in `functions-webhook/src/emailVariants.ts`.
 *
 * A comment is the right marker because it survives being stored, hashed and
 * diffed as ordinary text, and because a bug that failed to strip one produces a
 * message that renders correctly and carries an invisible comment — rather than
 * one that shows a reader the section meant for somebody else.
 */
import {
  EMAIL_THEME,
  overlayOf,
  paddingOf,
  SOCIAL_LINKS,
  WORDMARK,
  type EmailBlock,
  type EmailDocument,
  type ReaderSegment,
} from '@/types/emailBlocks'
import { previewMerge } from './mergeFields'

/** `&`, `<`, `>` and quotes, for anything going into an attribute or a body. */
export function esc(v: string): string {
  return v
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * A colour we are willing to put in a style attribute.
 *
 * Anything that is not a hex colour becomes the fallback. The input comes from a
 * colour picker, so this is not about the user — it is about what a stored
 * document can contain after a round trip through somewhere else, and
 * `background: red; } body { display:none` is a style attribute that does more
 * than colour a band.
 */
export function colour(v: string, fallback: string): string {
  return /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(v.trim()) ? v.trim() : fallback
}

/**
 * A link we are willing to follow.
 *
 * `http`, `https` and `mailto` only. A `javascript:` href does nothing in an
 * inbox and a great deal in the preview pane of the person composing it.
 * A merge tag is let through — it is replaced before anything is sent.
 */
export function href(v: string): string {
  const s = v.trim()
  if (/^\{\{[A-Za-zÆØÅæøå0-9_-]+\}\}$/.test(s)) return s
  return /^(https?:|mailto:)/i.test(s) ? s : ''
}

const alignOf = (b: EmailBlock) =>
  b.align === 'center' ? 'center' : b.align === 'right' ? 'right' : 'left'

const T = EMAIL_THEME
const font = `font-family:${T.font}`

/** A tinted panel, as a stat cell and a card both sit on. */
const panel = (inner: string, pad = '16px 18px', bg: string = T.panel) =>
  `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" ` +
  `style="border-collapse:separate;border-radius:12px;background:${bg}">` +
  `<tr><td style="padding:${pad};border-radius:12px">${inner}</td></tr></table>`

/**
 * An address safe inside `url('…')` in a style attribute.
 *
 * `href` has already limited it to http(s); this keeps a quote, a bracket or a
 * space in the path from ending the `url()` early. `esc` runs afterwards for
 * the attribute itself.
 */
const cssUrl = (u: string) =>
  u.replace(/['()\\\s]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`)

/** `#RRGGBB` or `#RGB` with an opacity, as `rgba()`. */
function rgba(hex: string, opacity: number): string {
  let h = hex.replace('#', '')
  if (h.length === 3) h = h.replace(/./g, (c) => c + c)
  const n = parseInt(h, 16)
  const a = Math.round(Math.min(100, Math.max(0, opacity))) / 100
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`
}

/** The shape of a picture as height over width, from the crop or the measurement. */
function aspectOf(b: EmailBlock): number {
  const m = /^(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)$/.exec(b.ratio.trim())
  if (m && Number(m[1]) > 0) return Number(m[2]) / Number(m[1])
  const a = overlayOf(b).aspect
  // An unmeasured picture is drawn 16:9 rather than at no height at all.
  return a > 0 && a < 5 ? a : 9 / 16
}

/**
 * A picture with text laid over it — 13 Oct 2026.
 *
 * Email cannot stack an element on an `<img>`: Outlook renders through Word,
 * which has no positioning. So the picture becomes the **background of a
 * table cell** of the picture's own height, and the text sits in that cell,
 * placed with the cell's own `align` and `valign` — the nine positions of cell
 * alignment in Word.
 *
 * Three layers, because no one of them reaches every inbox:
 *
 *   `background=` + `background-image`  Gmail, Apple Mail, Outlook on the web
 *   VML `v:rect` in an `[if mso]` block  Outlook on Windows, which ignores CSS
 *                                        backgrounds entirely
 *   `bgcolor`                            images off: the text on the box colour
 *
 * The box's colour is `rgba()` so it can be see-through. Outlook on Windows
 * does not read `rgba()` and shows no box there — the text straight on the
 * picture — which is the safe way to be wrong. A box at 100% also carries
 * `bgcolor`, so Outlook paints that one.
 *
 * The height is fixed, so on a narrow phone the picture is cropped at the sides
 * (`background-size: cover`) rather than squashed, and the text stays on it.
 */
function overlaid(b: EmailBlock, src: string, width: number): string {
  const o = overlayOf(b)
  const w = Math.max(1, Math.round(width))
  const h = Math.max(1, Math.round(w * aspectOf(b)))
  const url = esc(cssUrl(src))
  const boxHex = colour(o.background, '#000000')
  const textHex = colour(o.color, '#FFFFFF')
  const size = Math.min(72, Math.max(10, Math.round(o.size) || 28))
  const opacity = Math.min(100, Math.max(0, Math.round(o.opacity)))
  const h1 = o.hAlign === 'left' || o.hAlign === 'right' ? o.hAlign : 'center'
  const v1 = o.vAlign === 'top' || o.vAlign === 'bottom' ? o.vAlign : 'middle'

  // Plain text in, line breaks kept. Merge tags survive `esc` untouched.
  const words = esc(o.text).replace(/\r?\n/g, '<br>')
  const to = href(b.href)
  const inner = to
    ? `<a href="${esc(to)}" style="color:${textHex};text-decoration:none">${words}</a>`
    : words

  const box =
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="${h1}" ` +
    `style="border-collapse:separate${h1 === 'center' ? ';margin:0 auto' : ''}"><tr>` +
    `<td${opacity >= 100 ? ` bgcolor="${boxHex}"` : ''} align="${h1}" style="` +
    (opacity > 0 ? `background:${rgba(boxHex, opacity)};` : '') +
    `padding:${opacity > 0 ? '12px 18px' : '0'};border-radius:8px;${font};font-size:${size}px;` +
    `font-weight:700;line-height:1.2;color:${textHex};text-align:${h1}">${inner}</td></tr></table>`

  return (
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" ` +
    `style="border-collapse:collapse"><tr>` +
    `<td background="${esc(src)}" bgcolor="${boxHex}" width="${w}" height="${h}" valign="${v1}" ` +
    `style="background-color:${boxHex};background-image:url('${url}');background-size:cover;` +
    `background-position:center;background-repeat:no-repeat;height:${h}px">` +
    `<!--[if gte mso 9]><v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" ` +
    `style="width:${w}px;height:${h}px"><v:fill type="frame" src="${esc(src)}" color="${boxHex}" />` +
    `<v:textbox inset="0,0,0,0"><![endif]-->` +
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" ` +
    `style="border-collapse:collapse"><tr>` +
    `<td align="${h1}" valign="${v1}" height="${h}" style="height:${h}px;padding:20px;` +
    `text-align:${h1};vertical-align:${v1}">${box}</td></tr></table>` +
    `<!--[if gte mso 9]></v:textbox></v:rect><![endif]-->` +
    `</td></tr></table>`
  )
}

/** The inside of one section — no band, no padding, just the content. */
function body(b: EmailBlock, width: number): string {
  switch (b.kind) {
    case 'text':
      return b.html || ''

    /**
     * The wordmark, left-aligned, on white or on indigo.
     *
     * An image rather than type: the mark is a drawn thing, and an inbox with
     * images off shows the alt text, which is the fallback the spec asks for.
     */
    case 'header': {
      const dark = b.variant === 'dark'
      return (
        `<img src="${esc(dark ? WORDMARK.dark : WORDMARK.light)}" alt="Skida" height="20" ` +
        `style="display:block;height:20px;width:auto;border:0;outline:none;text-decoration:none">`
      )
    }

    case 'heading':
      return (
        `<div style="${font};font-size:26px;font-weight:600;line-height:1.25;` +
        `color:${esc(colour(b.color, T.heading))}">${esc(b.label)}</div>`
      )

    case 'image': {
      const src = href(b.src)
      if (!src) return ''
      // Text over the picture is a different construction; see `overlaid`.
      if (overlayOf(b).text.trim()) return overlaid(b, src, width)
      /**
       * `display:block` kills the baseline gap under an image in Gmail, and the
       * cap keeps a 2000px photo from blowing the column open in Outlook.
       *
       * `natural` leaves the picture at its own size — which is what a logo or
       * a small illustration wants, and what stretching one to the full column
       * ruins. Everything else fills the width it is given; the difference
       * between `full` and the default is the band's padding, handled in
       * `section`, not here.
       */
      const natural = b.variant === 'natural'
      const img =
        `<img src="${esc(src)}" alt="${esc(b.alt)}"${natural ? '' : ' width="100%"'} ` +
        `style="display:block;${natural ? '' : 'width:100%;'}max-width:100%;height:auto;` +
        `border:0;outline:none;text-decoration:none">`
      // A picture that is a link is the commonest thing in a marketing email —
      // a hero somebody taps. The anchor has to wrap the image itself, because
      // a client that strips the surrounding table would strip the link with it.
      const to = href(b.href)
      return to ? `<a href="${esc(to)}" style="text-decoration:none">${img}</a>` : img
    }

    case 'button': {
      const to = href(b.href)
      const label = esc(b.label || 'Open')
      // A table around the link, not a styled anchor: Outlook ignores padding on
      // an inline element, which turns a button into underlined text. The 60px
      // pill and the 14px/32px padding are the handover's, and that padding is
      // what keeps it over the 44px tap target it asks for.
      if (!to) return `<span style="${font};opacity:.5">${label} — no link set</span>`
      const bg = esc(colour(b.color, T.primary))
      return (
        `<table role="presentation" cellpadding="0" cellspacing="0" border="0" ` +
        `style="border-collapse:separate${b.align === 'center' ? ';margin:0 auto' : ''}">` +
        `<tr><td bgcolor="${bg}" style="border-radius:60px;background:${bg}">` +
        `<a href="${esc(to)}" style="display:inline-block;padding:14px 32px;${font};` +
        `font-size:16px;font-weight:600;color:#FFFFFF;text-decoration:none;border-radius:60px">${label}</a>` +
        `</td></tr></table>`
      )
    }

    /**
     * The dated notice — "the most-read line" in five of the twenty-three.
     *
     * A label in caps over one large value, in a pale-blue box. The dotted
     * underline marks a merged value, which is the handover's way of saying
     * *this came from the register, not from the writer*.
     */
    case 'notice':
      return panel(
        `<div style="${font};font-size:12px;font-weight:700;letter-spacing:.08em;` +
          `color:${T.body};padding-bottom:2px">${esc(b.label)}</div>` +
          `<div style="${font};font-size:22px;font-weight:700;color:${T.indigo};` +
          `border-bottom:1px dotted ${T.dotted};display:inline-block">${esc(b.html)}</div>`,
        '18px 24px',
        T.pale,
      )

    /**
     * One to three cells across.
     *
     * **A missing value drops its cell** rather than printing a dash — the
     * handover is explicit about it, and a row of dashes is a report about our
     * own data collection rather than about the reader's season.
     */
    case 'stats': {
      const cells = b.items.filter((i) => i.a.trim() !== '')
      if (cells.length === 0) return ''
      if (cells.length === 1) {
        const one = cells[0]!
        return panel(
          `<span style="${font};font-size:34px;font-weight:700;color:${T.heading}">${esc(one.a)}</span>` +
            `<span style="${font};font-size:15px;color:${T.body};padding-left:10px">${esc(one.b)}</span>`,
          '18px 24px',
        )
      }
      const w = Math.floor(100 / cells.length)
      const td = cells
        .map(
          (c, i) =>
            `<td width="${w}%" valign="top" style="padding-${i === 0 ? 'right' : 'left'}:6px">` +
            panel(
              `<div style="${font};font-size:26px;font-weight:700;color:${T.heading}">${esc(c.a)}</div>` +
                `<div style="${font};font-size:13px;color:${T.muted}">${esc(c.b)}</div>`,
            ) +
            `</td>`,
        )
        .join('')
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>${td}</tr></table>`
    }

    /** A route, with a thumbnail or without. Both forms are in the handover. */
    case 'card': {
      const title = b.items[0]?.a ?? ''
      const place = b.items[1]?.a ?? ''
      const detail = b.items[2]?.a ?? ''
      const lines =
        `<div style="${font};font-size:17px;font-weight:600;color:${T.heading}">${esc(title)}</div>` +
        (place ? `<div style="${font};font-size:14px;color:${T.muted};padding-top:3px">${esc(place)}</div>` : '') +
        (detail ? `<div style="${font};font-size:14px;color:${T.body};padding-top:3px">${esc(detail)}</div>` : '')
      const thumb = href(b.src)
      if (!thumb) return panel(lines, '18px 20px')
      return panel(
        `<table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>` +
          `<td width="96" valign="middle" style="padding-right:16px">` +
          `<img src="${esc(thumb)}" alt="${esc(b.alt)}" width="96" height="96" ` +
          `style="display:block;width:96px;height:96px;border-radius:8px;border:0;background:#D3D9DE"></td>` +
          `<td valign="middle">${lines}</td></tr></table>`,
        '14px',
      )
    }

    /** Numbered steps, as in "two taps left". The first is filled, the rest outlined. */
    case 'steps': {
      const rows = b.items
        .filter((i) => i.a.trim() !== '')
        .map((i, n) => {
          const first = n === 0
          const dot =
            `<td width="26" valign="middle" style="padding-right:12px">` +
            `<table role="presentation" cellpadding="0" cellspacing="0" border="0" ` +
            `style="border-collapse:separate;border-radius:13px;` +
            `${first ? `background:${T.primary}` : `border:1.5px solid ${T.primary}`}">` +
            `<tr><td align="center" width="26" height="26" style="${font};font-size:13px;font-weight:700;` +
            `color:${first ? '#FFFFFF' : T.primary};width:26px;height:26px">${n + 1}</td></tr></table></td>`
          const text =
            `<td valign="middle"><div style="${font};font-size:16px;font-weight:600;color:${T.heading}">${esc(i.a)}</div>` +
            (i.b ? `<div style="${font};font-size:14px;color:${T.muted}">${esc(i.b)}</div>` : '') +
            `</td>`
          return `<tr>${dot}${text}</tr><tr><td colspan="2" height="10" style="font-size:0;line-height:0">&nbsp;</td></tr>`
        })
        .join('')
      return `<table role="presentation" cellpadding="0" cellspacing="0" border="0">${rows}</table>`
    }

    /**
     * The foot of the message. Two forms, and the difference is legal.
     *
     * Marketing carries an unsubscribe; transactional says what the message is
     * about instead, because there is nothing to unsubscribe from in a receipt.
     */
    case 'footer': {
      const transactional = b.variant === 'transactional'
      // Both go to our own preference page, filled in per recipient by
      // `withFooterLinks`. The transactional footer pointed at the app's
      // settings screen, which has nothing to say about email at all.
      const links = transactional
        ? `<a href="{{preferences}}" style="color:${T.muted}">Email settings</a>`
        : `<a href="{{unsubscribe}}" style="color:${T.muted}">Unsubscribe</a>` +
          `&nbsp;·&nbsp;<a href="{{preferences}}" style="color:${T.muted}">Email settings</a>`
      // The profiles first, as a row of 24px marks — a table, because Outlook
      // spaces nothing else reliably.
      const social =
        // `align` only to centre: on a table, left and right float it, and the
        // address would wrap up beside the icons.
        `<table role="presentation" cellpadding="0" cellspacing="0" border="0"${alignOf(b) === 'center' ? ' align="center"' : ''} ` +
        `style="margin-bottom:12px${alignOf(b) === 'right' ? ';margin-left:auto' : ''}"><tr>` +
        SOCIAL_LINKS.map(
          (s, i) =>
            `<td style="padding-${i === 0 ? 'right' : 'left'}:${i === 0 ? 0 : 12}px">` +
            `<a href="${esc(s.href)}" title="Skida on ${esc(s.name)}" style="text-decoration:none">` +
            `<img src="${esc(s.icon)}" alt="${esc(s.name)}" width="24" height="24" ` +
            `style="display:block;width:24px;height:24px;border:0;outline:none"></a></td>`,
        ).join('') +
        `</tr></table>`
      return (
        social +
        `<div style="${font};font-size:12px;line-height:1.5;color:${T.muted}">${esc(b.html)}` +
        (transactional ? ' · About your active subscription' : '') +
        `</div><div style="${font};font-size:12px;padding-top:6px">${links}</div>`
      )
    }

    case 'divider':
      return (
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">` +
        `<tr><td style="border-top:1px solid ${esc(colour(b.color, T.line))};font-size:0;line-height:0">&nbsp;</td></tr></table>`
      )

    case 'spacer':
      return `<div style="font-size:0;line-height:0">&nbsp;</div>`
  }
}

/** One section, as its own full-width band. */
function section(b: EmailBlock, width: number): string {
  // A dark header paints its own band whatever the block's colour says — a
  // white wordmark on white is the one way that component can be wrong.
  const bg =
    b.kind === 'header' && b.variant === 'dark'
      ? T.indigo
      : colour(b.background, T.surface)
  const fg = colour(b.color, T.body)
  // Equal top and bottom keep the old shorthand, so a section nobody touched renders byte for byte as before.
  const pad = paddingOf(b)
  // A button and a heading paint their own colours from `color`, so the band
  // must not also take it or a white-on-white button appears.
  const text = b.kind === 'button' || b.kind === 'heading' ? T.body : fg
  // 40px down the sides of the content column, per the handover. A rule and a
  // spacer sit closer in, where the inset would read as a gap in the rule, and
  // a full-width picture takes none at all — that is what full width means.
  const side =
    b.kind === 'image' && b.variant === 'full'
      ? 0
      : b.kind === 'divider' || b.kind === 'spacer'
        ? 28
        : 40
  // The content's own width in px — an overlaid picture needs it for its height
  // and for Outlook's VML, which takes no percentages.
  const inner = body(b, width - 2 * side)
  const cell =
    `<td align="${alignOf(b)}" bgcolor="${esc(bg)}" ` +
    `style="background:${esc(bg)};color:${esc(text)};padding:${pad.top === pad.bottom ? `${pad.top}px ${side}px` : `${pad.top}px ${side}px ${pad.bottom}px`};` +
    `${font};font-size:16px;line-height:1.55">${inner}</td>`
  return (
    `<table role="presentation" width="${width}" cellpadding="0" cellspacing="0" border="0" ` +
    `style="width:100%;max-width:${width}px;border-collapse:collapse"><tr>${cell}</tr></table>`
  )
}

/**
 * The comment pair the server cuts on. Kept in one place; grep `skida:if`.
 *
 * `<!--skida:if=paid,trial-->`, and since 28 Oct 2026 with places after a
 * semicolon: `<!--skida:if=paid;lang=no,sv;country=NO-->`. Each part that is
 * present has to hold; within a part, any one is enough.
 */
export function conditionOf(b: Pick<EmailBlock, 'visibleTo' | 'languages' | 'countries'>): string {
  const parts = [b.visibleTo.join(',')]
  if (b.languages?.length) parts.push(`lang=${b.languages.join(',')}`)
  if (b.countries?.length) parts.push(`country=${b.countries.join(',')}`)
  return parts.length === 1 ? parts[0]! : parts.join(';')
}
export const IF_OPEN = (condition: string) => `<!--skida:if=${condition}-->`
export const IF_CLOSE = '<!--skida:end-->'

/**
 * Whether a reader with these segments gets the section — the server's
 * `stripFor`, applied to the block. A dimension the reader says nothing about
 * (the preview with no language picked) does not hide anything.
 */
export function blockShownTo(
  b: Pick<EmailBlock, 'visibleTo' | 'languages' | 'countries'>,
  as: readonly string[],
): boolean {
  const langs = as.filter((s) => s.startsWith('lang:'))
  const countries = as.filter((s) => s.startsWith('country:'))
  const rest = as.filter((s) => !s.startsWith('lang:') && !s.startsWith('country:'))
  if (b.visibleTo.length && rest.length && !b.visibleTo.some((t) => rest.includes(t))) return false
  if (b.languages?.length && langs.length && !b.languages.some((l) => langs.includes(`lang:${l}`))) return false
  if (b.countries?.length && countries.length && !b.countries.some((c) => countries.includes(`country:${c}`)))
    return false
  return true
}

/**
 * The whole message, with every section present and conditions marked.
 *
 * This is what is stored on the draft and hashed for the test gate: one string
 * that contains every variant, so two readers' versions cannot drift apart
 * between the test and the send.
 */
export function renderEmail(doc: EmailDocument): string {
  const width = Math.min(Math.max(Math.round(doc.width) || 600, 320), 800)
  const page = colour(doc.background, T.page)

  const blocks = doc.blocks
    .map((b) => {
      const html = section(b, width)
      const condition = conditionOf(b)
      return condition ? `${IF_OPEN(condition)}${html}${IF_CLOSE}` : html
    })
    .join('')

  // Hidden, first, and padded with zero-width joiners so the inbox does not
  // carry on into the first words of the body after it.
  const pre = (doc.preheader ?? '').trim()
  const preheader = pre
    ? `<div style="display:none;font-size:1px;line-height:1px;max-height:0;max-width:0;opacity:0;overflow:hidden;mso-hide:all;color:${esc(page)}">` +
      `${esc(pre)}${'&#847;&zwnj;&nbsp;'.repeat(40)}</div>`
    : ''

  return (
    preheader +
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" ` +
    `bgcolor="${esc(page)}" style="background:${esc(page)};width:100%;border-collapse:collapse">` +
    `<tr><td align="center" style="padding:24px 12px">` +
    `<table role="presentation" width="${width}" cellpadding="0" cellspacing="0" border="0" ` +
    `style="width:100%;max-width:${width}px;border-collapse:collapse;border-radius:12px;overflow:hidden">` +
    `<tr><td>${blocks}</td></tr></table></td></tr></table>`
  )
}

/**
 * Where the footer's links point in the preview.
 *
 * `{{unsubscribe}}` and `{{preferences}}` are filled in per recipient by
 * `withFooterLinks` on the way out, so the stored document holds the tags and
 * not a URL. A browser asked to render `href="{{preferences}}"` resolves it as a
 * *relative* path against whatever page the preview is on, which is how the
 * footer came to point at `internal.skida.app/marketing/%7B%7Bpreferences%7D%7D`
 * — a link that was never going to be sent to anybody, and looked broken because
 * it was.
 *
 * So the preview substitutes a sample of the real thing. Not a live token: one
 * would mean minting per-address proof in the browser, and the preview has no
 * recipient to mint it for.
 */
const SAMPLE_PREF_URL =
  'https://europe-west3-skida-internal.cloudfunctions.net/crmUnsubscribe' +
  '?e=reader%40example.com&t=sample'

/**
 * The merge tags a preview has no recipient to fill, shown as what they become.
 *
 * The two footer links first, then the reader's own facts — `{{routes}}`,
 * `{{expires}}` and the rest — filled from the sample values so the writer reads
 * a sentence rather than a template. See `mergeFields.ts`; a tag neither of them
 * knows is left visible, which is how a typo gets caught here instead of in an
 * inbox.
 */
function previewLinks(html: string, values?: Record<string, string>): string {
  const linked = html
    .split('{{unsubscribe}}')
    .join(SAMPLE_PREF_URL)
    .split('{{preferences}}')
    .join(SAMPLE_PREF_URL)
  return previewMerge(linked, values)
}

/**
 * The message as one reader receives it — for the preview.
 *
 * The same cut the server makes, so what the screen shows is what will be sent.
 * `null` means "as composed", with every section shown.
 *
 * Takes a **set** of segments rather than one, because a real reader is several
 * things at once — paying, in a PRO company, has drawn routes — and previewing
 * them one at a time would never show the message any actual person receives. A
 * section is kept when its list names any of them, which is `stripFor`'s rule.
 *
 * `values` fills the merge tags with a named reader's own figures instead of the
 * examples. Without it the samples are used, which is right when there is nobody
 * in particular to preview as.
 *
 * **Preview only.** `renderEmail` is what gets stored and sent, and it keeps the
 * merge tags — a document with a sample address baked into its footer would mail
 * everybody a link that unsubscribes nobody.
 */
export function renderForTier(
  doc: EmailDocument,
  tier: ReaderSegment | readonly ReaderSegment[] | null,
  values?: Record<string, string>,
): string {
  const as = tier === null ? null : typeof tier === 'string' ? [tier] : tier
  const cut =
    as && as.length > 0
      ? renderEmail({
          ...doc,
          blocks: doc.blocks.filter((b) => blockShownTo(b, as)),
        })
      : renderEmail(doc)
  return previewLinks(cut, values)
}
